import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheService } from 'ionic-cache';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  Recommendation_,
  ToolContainer,
  Parameters_,
  UserDataLoggingInfo,
  ParameterLabel,
  RecommendationLabel,
  Machine_
} from 'src/service/formulas/objects';
import { ServiceProvider } from 'src/service/provider.interface';
import math from 'mathjs';
import { Formulas } from '../../service/formulas/formulas';

@Injectable()
export class ToolProvider extends ServiceProvider {
  private logDataEndpoint: string;
  private allProductHierarchyGroupsEndpoint: string;
  private paramsAndRecsGroupingEndpoint: string;
  private getProductkeyCountEndpoint: string;
  private token: any;
  private userDataLoggingInfo: UserDataLoggingInfo;
  constructor(public cache: CacheService, private http: HttpClient, private formulas: Formulas) {
    super();
    this.logDataEndpoint = this.config.apiEndpoints['logUserData'];
    this.allProductHierarchyGroupsEndpoint = this.config.apiEndpoints['allProductHierarchyGroups'];
    this.paramsAndRecsGroupingEndpoint = this.config.apiEndpoints['paramsAndRecsProductkeyGrouping'];
    this.getProductkeyCountEndpoint = this.config.apiEndpoints['endmillMapModeTwoCount'];
    this.cache.getItem('loginInfo').then(res => (this.token = res));
  }

  public logUserData = (
    logReason: string,
    settingsHolder: ToolContainer,
    recommendations: Recommendation_
  ): Observable<any> => {
    this.userDataLoggingInfo = this.emptyUserData();
    const headers: HttpHeaders = this.createAuthorizationHeader(this.token.access_token);

    this.userDataLoggingInfo.logType = logReason;
    this.userDataLoggingInfo.userId = this.token.id;

    let userToolPath = '';
    if (settingsHolder.hasStrategyOption && !!settingsHolder.currentStrategy) {
      userToolPath = settingsHolder.currentStrategy.operationName;
      this.userDataLoggingInfo.operationId = settingsHolder.currentStrategy.strategyId;
    } else if (!settingsHolder.hasStrategyOption && !!settingsHolder.currenttoolpath) {
      userToolPath = settingsHolder.currenttoolpath.operationName;
      this.userDataLoggingInfo.operationId = settingsHolder.currenttoolpath.toolPathId;
    }

    if (settingsHolder.mapMode === 1 && !!settingsHolder.parameters && !!recommendations) {
      this.generateMapModeOneParams(settingsHolder.parameters, userToolPath);
      this.generateMapModeOneRecs(recommendations, userToolPath);
    } else if (settingsHolder.mapMode === 2 && settingsHolder.mapModeTwoValues) {
      this.generateMapModeTwoParams(settingsHolder.mapModeTwoValues.parameterLabels);
      this.generateMapModeTwoRecs(
        settingsHolder.mapModeTwoValues.recommendationLabels,
        settingsHolder.machine,
        recommendations.speedAggression,
        recommendations.feedAggression
      );
    }

    // Fill in the rest of the data that will be logged
    // Adding in checks for data filled for cases like user logging out and not having all the information selected

    if (!!settingsHolder.hardness) {
      this.userDataLoggingInfo.HBS = settingsHolder.hardness.hbs;
      this.userDataLoggingInfo.HBW = settingsHolder.hardness.hbw;
      this.userDataLoggingInfo.HRB = settingsHolder.hardness.hrb;
      this.userDataLoggingInfo.HRC = settingsHolder.hardness.hrc;
    }

    if (!!recommendations) {
      this.userDataLoggingInfo.feedSliderValue = recommendations.feedAggression;
      this.userDataLoggingInfo.speedSliderValue = recommendations.speedAggression;
    }

    if (!!settingsHolder.material) {
      this.userDataLoggingInfo.materialId = settingsHolder.material.id;
    }

    if (!!settingsHolder.tool) {
      this.userDataLoggingInfo.productKey = settingsHolder.tool.id;
    }

    this.userDataLoggingInfo.MaxIPM = settingsHolder.machine.maxIPM;
    this.userDataLoggingInfo.holder = settingsHolder.machine.holder;
    this.userDataLoggingInfo.maxRPM = settingsHolder.machine.maxRPM;
    this.userDataLoggingInfo.spindle = settingsHolder.machine.spindle;
    this.userDataLoggingInfo.workHoldingSliderPosition = settingsHolder.machine.fixtureBar
      ? settingsHolder.machine.fixtureBar / 100
      : null;

    return this.http
      .post(this.logDataEndpoint, this.userDataLoggingInfo, { headers: headers })
      .pipe(
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      );
  };

  public getProductkeyCount = async (): Promise<any> => {
    const headers: HttpHeaders = this.createAuthorizationHeader(this.token.access_token);

    const url: string = this.getProductkeyCountEndpoint;

    try {
      return this.http
        .get(url, {
          headers: headers
        })
        .toPromise();
    } catch (err) {
      observableThrowError(err.error.error_description || 'Server error');
    }
  };

  public getAllProductHierarchyGroups = async (): Promise<any> => {
    const headers: HttpHeaders = this.createAuthorizationHeader(this.token.access_token);

    const url: string = this.allProductHierarchyGroupsEndpoint;

    try {
      return this.http
        .get(url, {
          headers: headers
        })
        .toPromise();
    } catch (err) {
      observableThrowError(err.error.error_description || 'Server error');
    }
  };

  public getSetOfParamsAndRecs = async (offset: number): Promise<any | undefined> => {
    const headers: HttpHeaders = this.createAuthorizationHeader(this.token.access_token);

    const url: string = this.paramsAndRecsGroupingEndpoint;

    try {
      const response = await this.http
        .get(url, {
          headers: headers,
          params: { offset: offset.toString() }
        })
        .toPromise();
      return response;
    } catch (err) {
      return null;
    }
  };

  getParametersAndRecommendationsForEndmill = (productKey: number): Promise<any> => {
    const url: string = this.config.apiEndpoints['paramsAndRecs'];
    const headers: HttpHeaders = this.createAuthorizationHeader(this.token.access_token);

    const httpParams = {
      productKey: productKey.toString()
    };

    try {
      return this.http
        .get(url + '/get-endmill', {
          params: httpParams,
          headers: headers
        })
        .toPromise();
    } catch (err) {
      observableThrowError(err.error.error_description || 'Server error');
    }
  };

  private generateMapModeOneParams = (parameters: Parameters_, userToolPath: string) => {
    switch (userToolPath) {
      case 'chamfering':
        this.userDataLoggingInfo.parameterOneLabel = 'Stick Out';
        this.userDataLoggingInfo.parameterTwoLabel = 'Effective Diameter';
        this.userDataLoggingInfo.parameterThreeLabel = 'Chamfer Height';
        this.userDataLoggingInfo.parameterFourLabel = 'Chamfer Width';

        this.userDataLoggingInfo.parameterOneValue = Number(parameters.stickOut).toFixed(4);
        this.userDataLoggingInfo.parameterTwoValue = Number(parameters.effectiveDiameter).toFixed(4);
        this.userDataLoggingInfo.parameterThreeValue = Number(parameters.chamferHeight).toFixed(4);
        this.userDataLoggingInfo.parameterFourValue = Number(parameters.chamferWidth).toFixed(4);
        break;
      case '3d surfacing':
        //double check pdf for muSymbol
        this.userDataLoggingInfo.parameterOneLabel = 'Stick Out';
        this.userDataLoggingInfo.parameterTwoLabel = 'Axial Depth';
        this.userDataLoggingInfo.parameterThreeLabel = 'Scallop Height (Ra)';
        this.userDataLoggingInfo.parameterFourLabel = 'Tool Stepover';

        this.userDataLoggingInfo.parameterOneValue = Number(parameters.stickOut).toFixed(4);
        this.userDataLoggingInfo.parameterTwoValue = Number(parameters.adocdecimal).toFixed(4); //parameters.adocdecimal + ' (' + parameters.stepDown + '% step down)',
        this.userDataLoggingInfo.parameterThreeValue = Number(parameters.scallopHeight).toFixed(4);
        this.userDataLoggingInfo.parameterFourValue = Number(parameters.scallopWidth).toFixed(4);
        break;
      case 'helical plunge':
        this.userDataLoggingInfo.parameterOneLabel = 'Stick Out';
        this.userDataLoggingInfo.parameterTwoLabel = 'Ramp Angle';
        this.userDataLoggingInfo.parameterThreeLabel = 'Path Diameter';
        this.userDataLoggingInfo.parameterFourLabel = 'Hole Depth';

        this.userDataLoggingInfo.parameterOneValue = Number(parameters.stickOut).toFixed(4);
        this.userDataLoggingInfo.parameterTwoValue = Number(parameters.rampAngle).toFixed(4);
        this.userDataLoggingInfo.parameterThreeValue = Number(parameters.pathDiameter).toFixed(4);
        this.userDataLoggingInfo.parameterFourValue = Number(parameters.holeDepth).toFixed(4);
        break;
      default:
        this.userDataLoggingInfo.parameterOneLabel = 'Stick Out';
        this.userDataLoggingInfo.parameterTwoLabel = 'Axial Depth';
        this.userDataLoggingInfo.parameterThreeLabel = 'Radial Depth';
        this.userDataLoggingInfo.parameterFourLabel = 'Engagement Angle';

        this.userDataLoggingInfo.parameterOneValue = Number(parameters.stickOut).toFixed(4);
        this.userDataLoggingInfo.parameterTwoValue = Number(parameters.adocdecimal).toFixed(4); //parameters.adocdecimal + ' (' + parameters.stepDown + '% step down)'
        this.userDataLoggingInfo.parameterThreeValue = Number(parameters.rdocdecimal).toFixed(4); //parameters.rdocdecimal + ' (' + parameters.stepOver + '% step over)'
        this.userDataLoggingInfo.parameterFourValue = Number(parameters.engagementAngle).toFixed(4);
        break;
    }
  };

  private generateMapModeOneRecs = (recommendations: Recommendation_, userToolPath: string) => {
    let chipThickness: string = recommendations.chipThickness ? recommendations.chipThickness.toFixed(4) : '0';
    switch (userToolPath) {
      case 'chamfering':
        this.userDataLoggingInfo.recommendationOneLabel = 'Surface Speed';
        this.userDataLoggingInfo.recommendationTwoLabel = 'Speed';
        this.userDataLoggingInfo.recommendationThreeLabel = 'Feed Per Tooth';
        this.userDataLoggingInfo.recommendationFourLabel = 'Feed Per Minute';

        this.userDataLoggingInfo.recommendationOneValue = math.round(recommendations.sfm, 0);
        this.userDataLoggingInfo.recommendationTwoValue = math.round(recommendations.rpm, 0);
        this.userDataLoggingInfo.recommendationThreeValue = math.round(recommendations.ipt, 4);
        this.userDataLoggingInfo.recommendationFourValue = math.round(recommendations.ipm, 0);
        break;
      case 'trochoidal slotting':
        this.userDataLoggingInfo.recommendationOneLabel = 'Trochoidal Slot Width';
        this.userDataLoggingInfo.recommendationTwoLabel = 'Surface Speed';
        this.userDataLoggingInfo.recommendationThreeLabel = 'Speed';
        this.userDataLoggingInfo.recommendationFourLabel = 'Feed Per Minute';
        this.userDataLoggingInfo.recommendationFiveLabel = 'Chip Thickness';
        this.userDataLoggingInfo.recommendationSixLabel = 'MRR';

        this.userDataLoggingInfo.recommendationOneValue = math.round(recommendations.trochoidalSlotWidth, 4);
        this.userDataLoggingInfo.recommendationTwoValue = math.round(recommendations.sfm, 0);
        this.userDataLoggingInfo.recommendationThreeValue = math.round(recommendations.rpm, 0);
        this.userDataLoggingInfo.recommendationFourValue = math.round(recommendations.ipmFeedInsideSlot, 0);
        this.userDataLoggingInfo.recommendationFiveValue = chipThickness;
        this.userDataLoggingInfo.recommendationSixValue = math.round(recommendations.mrr, 3);
        break;
      case 'helical plunge':
        this.userDataLoggingInfo.recommendationOneLabel = 'Surface Speed';
        this.userDataLoggingInfo.recommendationTwoLabel = 'Speed';
        this.userDataLoggingInfo.recommendationThreeLabel = 'Feed Per Tooth';
        this.userDataLoggingInfo.recommendationFourLabel = 'Feed Per Minute';
        this.userDataLoggingInfo.recommendationFiveLabel = 'MRR';

        this.userDataLoggingInfo.recommendationOneValue = math.round(recommendations.sfm, 0);
        this.userDataLoggingInfo.recommendationTwoValue = math.round(recommendations.rpm, 0);
        this.userDataLoggingInfo.recommendationThreeValue = math.round(recommendations.ipt, 4);
        this.userDataLoggingInfo.recommendationFourValue = math.round(recommendations.ipm, 0);
        this.userDataLoggingInfo.recommendationFiveValue = math.round(recommendations.mrr, 3);
        break;
      case '3d surfacing':
        this.userDataLoggingInfo.recommendationOneLabel = 'Surface Speed';
        this.userDataLoggingInfo.recommendationTwoLabel = 'Speed';
        this.userDataLoggingInfo.recommendationThreeLabel = 'Feed Per Tooth';
        this.userDataLoggingInfo.recommendationFourLabel = 'Feed Per Minute';
        this.userDataLoggingInfo.recommendationFiveLabel = 'Chip Thickness';

        this.userDataLoggingInfo.recommendationOneValue = math.round(recommendations.sfm, 0);
        this.userDataLoggingInfo.recommendationTwoValue = math.round(recommendations.rpm, 0);
        this.userDataLoggingInfo.recommendationThreeValue = math.round(recommendations.ipt, 4);
        this.userDataLoggingInfo.recommendationFourValue = math.round(recommendations.ipm, 0);
        this.userDataLoggingInfo.recommendationFiveValue = chipThickness;
        break;
      default:
        this.userDataLoggingInfo.recommendationOneLabel = 'Surface Speed';
        this.userDataLoggingInfo.recommendationTwoLabel = 'Speed';
        this.userDataLoggingInfo.recommendationThreeLabel = 'Feed Per Tooth';
        this.userDataLoggingInfo.recommendationFourLabel = 'Feed Per Minute';
        this.userDataLoggingInfo.recommendationFiveLabel = 'Chip Thickness';
        this.userDataLoggingInfo.recommendationSixLabel = 'MRR';

        this.userDataLoggingInfo.recommendationOneValue = math.round(recommendations.sfm, 0);
        this.userDataLoggingInfo.recommendationTwoValue = math.round(recommendations.rpm, 0);
        this.userDataLoggingInfo.recommendationThreeValue = math.round(recommendations.ipt, 4);
        this.userDataLoggingInfo.recommendationFourValue = math.round(recommendations.ipm, 0);
        this.userDataLoggingInfo.recommendationFiveValue = chipThickness;
        this.userDataLoggingInfo.recommendationSixValue = math.round(recommendations.mrr, 3);
        break;
    }
  };

  private generateMapModeTwoParams = (parameters: ParameterLabel[]) => {
    this.userDataLoggingInfo.parameterOneLabel = parameters[0] ? parameters[0].parameterLabel : '';
    this.userDataLoggingInfo.parameterTwoLabel = parameters[1] ? parameters[1].parameterLabel : '';
    this.userDataLoggingInfo.parameterThreeLabel = parameters[2] ? parameters[2].parameterLabel : '';
    this.userDataLoggingInfo.parameterFourLabel = parameters[3] ? parameters[3].parameterLabel : '';
    this.userDataLoggingInfo.parameterFiveLabel = parameters[4] ? parameters[4].parameterLabel : '';
    this.userDataLoggingInfo.parameterSixLabel = parameters[5] ? parameters[5].parameterLabel : '';

    this.userDataLoggingInfo.parameterOneValue = parameters[0] ? parameters[0].parameterValue : '';
    this.userDataLoggingInfo.parameterTwoValue = parameters[1] ? parameters[1].parameterValue : '';
    this.userDataLoggingInfo.parameterThreeValue = parameters[2] ? parameters[2].parameterValue : '';
    this.userDataLoggingInfo.parameterFourValue = parameters[3] ? parameters[3].parameterValue : '';
    this.userDataLoggingInfo.parameterFiveValue = parameters[4] ? parameters[4].parameterValue : '';
    this.userDataLoggingInfo.parameterSixValue = parameters[5] ? parameters[5].parameterValue : '';
  };

  private generateMapModeTwoRecs = (
    recommendations: RecommendationLabel[],
    machine: Machine_,
    speedAggression: number,
    feedAggression: number
  ) => {
    if (!!machine.holderAdjustment) {
      const selectedHolderAdjustment = machine.holderAdjustment.find(ha => ha.adjust === 'SFM');
      const selectedSpindleAdjustment = machine.spindleAdjustment.find(sa => sa.adjust === 'FPT');

      const sfmRation = this.formulas.calculateSfmRatio(
        recommendations,
        selectedHolderAdjustment.factor,
        selectedSpindleAdjustment.factor,
        machine.maxRPM,
        speedAggression,
        feedAggression
      );
      const fptRation = this.formulas.calculateFptRatio(
        recommendations,
        selectedHolderAdjustment.factor,
        selectedSpindleAdjustment.factor,
        machine.maxIPM,
        speedAggression,
        feedAggression,
        sfmRation
      );

      this.userDataLoggingInfo.recommendationOneLabel = recommendations[0]
        ? recommendations[0].recommendationLabel
        : '';
      this.userDataLoggingInfo.recommendationTwoLabel = recommendations[1]
        ? recommendations[1].recommendationLabel
        : '';
      this.userDataLoggingInfo.recommendationThreeLabel = recommendations[2]
        ? recommendations[2].recommendationLabel
        : '';
      this.userDataLoggingInfo.recommendationFourLabel = recommendations[3]
        ? recommendations[3].recommendationLabel
        : '';
      this.userDataLoggingInfo.recommendationFiveLabel = recommendations[4]
        ? recommendations[4].recommendationLabel
        : '';
      this.userDataLoggingInfo.recommendationSixLabel = recommendations[5]
        ? recommendations[5].recommendationLabel
        : '';

      this.userDataLoggingInfo.recommendationOneValue = recommendations[0]
        ? this.calculateRecommendationValue(
            recommendations[0],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
      this.userDataLoggingInfo.recommendationTwoValue = recommendations[1]
        ? this.calculateRecommendationValue(
            recommendations[1],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
      this.userDataLoggingInfo.recommendationThreeValue = recommendations[2]
        ? this.calculateRecommendationValue(
            recommendations[2],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
      this.userDataLoggingInfo.recommendationFourValue = recommendations[3]
        ? this.calculateRecommendationValue(
            recommendations[3],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
      this.userDataLoggingInfo.recommendationFiveValue = recommendations[4]
        ? this.calculateRecommendationValue(
            recommendations[4],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
      this.userDataLoggingInfo.recommendationSixValue = recommendations[5]
        ? this.calculateRecommendationValue(
            recommendations[5],
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            speedAggression,
            feedAggression,
            sfmRation,
            fptRation
          )
        : '';
    }
  };

  private calculateRecommendationValue = (
    recommendation: RecommendationLabel,
    holderFactor: number,
    spindleFactor: number,
    speedAggression: number,
    feedAggression: number,
    sfmRation: number,
    fptRation: number
  ): string => {
    const recValue =
      Number(recommendation.recommendationValue) *
      (recommendation.recommendationAdjustment.includes('SFM') ? holderFactor * speedAggression * sfmRation : 1) *
      (recommendation.recommendationAdjustment.includes('FPT') ? spindleFactor * feedAggression * fptRation : 1);
    return math.round(recValue, Number(recommendation.recommendationPrecision));
  };

  private emptyUserData = (): UserDataLoggingInfo => {
    return {
      userId: '',
      logType: '',
      productKey: null,
      materialId: null,
      HBW: null,
      HBS: null,
      HRB: null,
      HRC: null,
      operationId: null,
      holder: '',
      spindle: '',
      maxRPM: null,
      MaxIPM: null,
      workHoldingSliderPosition: null,
      parameterOneLabel: '',
      parameterOneValue: '',
      parameterTwoLabel: '',
      parameterTwoValue: '',
      parameterThreeLabel: '',
      parameterThreeValue: '',
      parameterFourLabel: '',
      parameterFourValue: '',
      parameterFiveLabel: '',
      parameterFiveValue: '',
      parameterSixLabel: '',
      parameterSixValue: '',
      recommendationOneLabel: '',
      recommendationOneValue: '',
      recommendationTwoLabel: '',
      recommendationTwoValue: '',
      recommendationThreeLabel: '',
      recommendationThreeValue: '',
      recommendationFourLabel: '',
      recommendationFourValue: '',
      recommendationFiveLabel: '',
      recommendationFiveValue: '',
      recommendationSixLabel: '',
      recommendationSixValue: '',
      speedSliderValue: null,
      feedSliderValue: null
    };
  };
}
